import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import {
  useDialedDigitExtractionServerFieldCreateNew,
} from 'routes/ProcessOptions/hooks/useDialedDigitExtractionServerFieldCreateNew';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import ProcessOptionsForm from 'routes/ProcessOptions/components/ProcessOptionsForm';
import { Ref } from 'routes/ProcessOptions/components/ProcessOptionsForm/ProcessOptionsForm';
import { RootState } from 'data/root.reducers';
import * as processOptionsSelectors from 'data/processOption/processOption.selectors';
import * as processOptionsActions from 'data/processOption/processOption.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { ProcessOption } from 'data/processOption/processOption.types';
import { processOptionsTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/dialedDigitExtractionServerTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/dialedDigitExtractionServerDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  processOptionTemplateDef: adaptTemplateVisibilityMode(processOptionsTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedProcessOption: processOptionsSelectors
    .getSelectedProcessOptions(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedProcessOption: processOptionsSelectors
    .getSelectedProcessOptions(state)[0],
  processOption: processOptionsSelectors.getProcessOptions(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToProcessOptionsList: processOptionsActions.goToProcessOptionsList,
  updateProcessOptions: processOptionsActions.updateProcessOptions,
  getProcessOptions: processOptionsActions.getProcessOptions,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const ProcessOptionsEdit = ({
  history,
  selectedProcessOption,
  originalSelectedProcessOption,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  processOptionTemplateDef,
  goToProcessOptionsList,
  updateProcessOptions,
  getProcessOptions,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<ProcessOption>>({});
  const [template, setTemplate] = useState(processOptionTemplateDef);
  const { t } = useTranslation();

  const id = selectedProcessOption?.id;

  const [handleFieldCreateNew] = useDialedDigitExtractionServerFieldCreateNew(
    (field) => handleFieldUpdate(field),
  );

  useEffect(() => {
    if (id) {
      getProcessOptions(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getProcessOptions]);

  // useEffect(() => {
  //   setTemplate(adaptTemplateOptions(template, networkInterfaceList));
  // }, [networkInterfaceList]);

  useEffect(() => {
    if (selectedProcessOption != null && template != null) {
      setFormData(selectedProcessOption);

      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [selectedProcessOption, id]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const processOptionData = formRef.current.getValues();
    const payload = {
      data: processOptionData,
      auditDetails: getAuditDetailsToSubmit(
        processOptionData,
        originalSelectedProcessOption,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload) {
      updateProcessOptions(deliveryFunctionId, selectedProcessOption?.id, payload, history);
    }
  };

  const handleCancel = () => goToProcessOptionsList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as ProcessOption);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <ProcessOptionsForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="ProcessOptionsEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('processOptions.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(ProcessOptionsEdit));
